import styled, { css } from 'styled-components';
import React, { useContext, useRef, useState, useCallback, useImperativeHandle, useEffect, Fragment } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import isEqual from '@userzoomlibraries/toolkit-js-utils/is-equal';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import { darken } from 'polished';
import { jsxs, jsx } from 'react/jsx-runtime';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var RadioContext = /*#__PURE__*/React.createContext({});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Container = styled.label.attrs({
  className: 'gz-radio'
}).withConfig({
  componentId: "gz-radio-1679041977648__sc-vpz20d-0"
})(["align-items:center;display:inline-flex;vertical-align:middle;", ""], function (_ref) {
  var placement = _ref.placement;

  if (placement === 'right') {
    return "\n        flex-direction: row;\n      ";
  }

  if (placement === 'left') {
    return "\n        flex-direction: row-reverse;\n      ";
  }

  if (placement === 'top') {
    return "\n        flex-direction: column-reverse;\n      ";
  }

  if (placement === 'bottom') {
    return "\n        flex-direction: column;\n      ";
  }

  return '';
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Input = styled.input.attrs(function (_ref) {
  var invalid = _ref.invalid;
  return {
    className: 'gz-radio__input',
    type: 'radio',
    'aria-invalid': invalid
  };
}).withConfig({
  componentId: "gz-radio-1679041977648__sc-1tfnmm3-0"
})(["border:0;clip:rect(0 0 0 0);clip:rect(0px,0px,0px,0px);clip-path:inset(50%);height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;white-space:nowrap;width:1px;"]);

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Wrapper = styled.div.attrs({
  className: 'gz-radio__wrapper',
  'aria-hidden': true
}).withConfig({
  componentId: "gz-radio-1679041977648__sc-hnuey5-0"
})(["align-items:center;border:1px solid #b6b6b6;border-radius:50%;color:#fff;cursor:pointer;display:inline-flex;flex-shrink:0;height:16px;justify-content:center;transition:background-color 250ms ease 0s,border-color 250ms ease 0s,box-shadow 250ms ease 0s;user-select:none;width:16px;", "[aria-invalid=\"true\"] + &{border-color:#f00314;}", ":hover > ", "[aria-invalid=\"true\"]:not([checked]) + &{border-color:#b6b6b6;}", "[aria-invalid=\"true\"]:disabled + &,", ":disabled + &{background-color:#b6b6b6;border-color:#b6b6b6;cursor:not-allowed;}", "[checked] + &{background-color:#314fff;border-color:", ";}", "[aria-invalid=\"true\"][checked] + &{background-color:#f00314;border-color:", ";}", "[aria-invalid=\"true\"][checked]:disabled + &,", "[checked]:disabled + &{background-color:#b6b6b6;border-color:#b6b6b6;}", ":not(:disabled):focus + &{box-shadow:0 0 0 3px rgba(49,79,255,0.25);}"], Input, Container, Input, Input, Input, Input, darken(0.05, '#314fff'), Input, darken(0.05, '#f00314'), Input, Input, Input);

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Dot = styled.span.attrs({
  className: 'gz-radio__dot'
}).withConfig({
  componentId: "gz-radio-1679041977648__sc-1ld1y3f-0"
})(["background-color:#fff;border-radius:50%;display:inline-block;height:100%;opacity:0;position:relative;transform:scale(0.5);transition:opacity 250ms ease 0s,background-color 250ms ease 0s;vertical-align:middle;width:100%;", ":hover > ", ":not(:disabled):not([checked]) + ", " &{background-color:#b6b6b6;opacity:1;}", "[checked] + ", " &{opacity:1;}"], Container, Input, Wrapper, Input, Wrapper);

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Label = styled.label.attrs(function (_ref) {
  var htmlFor = _ref.htmlFor;
  return {
    className: 'gz-radio__label',
    htmlFor: htmlFor
  };
}).withConfig({
  componentId: "gz-radio-1679041977648__sc-151074x-0"
})(["color:#5b6c7f;cursor:pointer;font-size:14px;user-select:none;transition:color 250ms ease 0s;", " ", "[aria-invalid=\"true\"] ~ &,", "[aria-invalid=\"true\"][checked] ~ &{color:#f00314;}", ":hover > ", "[aria-invalid=\"true\"]:not([checked]) ~ &{color:#5b6c7f;}", "[aria-invalid=\"true\"]:disabled ~ &,", ":disabled ~ &,", "[aria-invalid=\"true\"][checked]:disabled ~ &,", "[checked]:disabled ~ &{color:#b6b6b6;cursor:not-allowed;}"], function (_ref2) {
  var placement = _ref2.placement;

  if (placement === 'right') {
    return css(["padding-left:6px;"]);
  }

  if (placement === 'left') {
    return css(["padding-right:6px;"]);
  }

  if (placement === 'top') {
    return css(["padding-bottom:6px;"]);
  }

  if (placement === 'bottom') {
    return css(["padding-top:6px;"]);
  }

  return '';
}, Input, Input, Container, Input, Input, Input, Input, Input);

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var useRadioInfo = function useRadioInfo() {
  return useContext(RadioContext);
};

var _excluded = ["as", "className", "value", "disabled", "children", "placement", "onClick"];

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var Item$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var AsComponent = _ref.as,
      className = _ref.className,
      value = _ref.value,
      disabled = _ref.disabled,
      children = _ref.children,
      placement = _ref.placement,
      onClick = _ref.onClick,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var ariaAttributes = useAriaAttributes(otherProps, {
    excludedAttributes: ['aria-checked', 'aria-disabled']
  });
  var dataAttributes = useDataAttributes(otherProps, {
    excludedAttributes: ['data-selected', 'data-disabled', 'data-invalid', 'data-focused']
  });
  var radioRef = useRef();

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      radioId = _useState2[0],
      setRadioId = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isFocused = _useState4[0],
      setIsFocused = _useState4[1];

  var _useRadioInfo = useRadioInfo(),
      _useRadioInfo$radioIn = _useRadioInfo.radioInfo,
      radioRefs = _useRadioInfo$radioIn.radioRefs,
      name = _useRadioInfo$radioIn.name,
      invalid = _useRadioInfo$radioIn.invalid,
      groupPlacement = _useRadioInfo$radioIn.placement,
      selectedValue = _useRadioInfo$radioIn.selectedValue,
      groupDisabled = _useRadioInfo$radioIn.disabled,
      onUpdateSelection = _useRadioInfo$radioIn.onUpdateSelection,
      onPreviousArrowPressed = _useRadioInfo$radioIn.onPreviousArrowPressed,
      onNextArrowPressed = _useRadioInfo$radioIn.onNextArrowPressed,
      onFocus = _useRadioInfo$radioIn.onFocus,
      onBlur = _useRadioInfo$radioIn.onBlur,
      setRadioInfo = _useRadioInfo.setRadioInfo;

  var isSelected = isEqual(value, selectedValue);
  var onChangeHandler = useCallback(function () {
    onUpdateSelection({
      radioId: radioId,
      radioRefs: radioRefs
    });
  }, [radioId, radioRefs, onUpdateSelection]);
  var onClickHandler = useCallback(function () {
    if (!disabled && !groupDisabled) {
      onClick({
        value: value
      });
      onChangeHandler();
    }
  }, [onClick, disabled, groupDisabled, value, onChangeHandler]);
  var onFocusHandler = useCallback(function () {
    if (!disabled && !groupDisabled) {
      setIsFocused(true);
      onFocus();
    }
  }, [onFocus, disabled, groupDisabled]);
  var onBlurHandler = useCallback(function (event) {
    var radioGroupElement = radioRef.current.closest('[role="radiogroup"');

    if (!radioGroupElement.contains(event.relatedTarget)) {
      onBlur(event);
    }

    setIsFocused(false);
  }, [onBlur]);
  var onKeyDownHandler = useCallback(function (event) {
    var isRightArrowPressed = event.key === 'ArrowRight' || event.code === 'ArrowRight' || event.keyCode === 39;
    var isLeftArrowPressed = event.key === 'ArrowLeft' || event.code === 'ArrowLeft' || event.keyCode === 37;
    var isUpArrowPressed = event.key === 'ArrowUp' || event.code === 'ArrowUp' || event.keyCode === 38;
    var isDownArrowPressed = event.key === 'ArrowDown' || event.code === 'ArrowDown' || event.keyCode === 40;
    var isSpaceBarPressed = event.key === ' ' || event.code === 'Space' || event.keyCode === 32;

    if (isSpaceBarPressed || isLeftArrowPressed || isRightArrowPressed || isUpArrowPressed || isDownArrowPressed) {
      event.preventDefault();

      if (isSpaceBarPressed) {
        onChangeHandler();
      }

      if (isUpArrowPressed || isLeftArrowPressed) {
        onPreviousArrowPressed({
          radioId: radioId,
          radioRefs: radioRefs
        });
      }

      if (isDownArrowPressed || isRightArrowPressed) {
        onNextArrowPressed({
          radioId: radioId,
          radioRefs: radioRefs
        });
      }
    }
  }, [radioId, radioRefs, onPreviousArrowPressed, onNextArrowPressed, onChangeHandler]);
  useImperativeHandle(ref, function () {
    return radioRef.current;
  });
  useEffect(function () {
    if (radioId) {
      setRadioInfo(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), {}, {
          radioRefs: _toConsumableArray(new Set([].concat(_toConsumableArray(prevState.radioRefs || []), [{
            id: radioId,
            ref: radioRef,
            value: value,
            disabled: disabled
          }])))
        });
      });
    }
  }, [radioId, value, disabled, setRadioInfo]);
  useEffect(function () {
    var radioRefCopy = radioRef.current;

    if (!disabled && !groupDisabled) {
      radioRefCopy.addEventListener('keydown', onKeyDownHandler);
    }

    return function () {
      if (!disabled && !groupDisabled) {
        radioRefCopy.removeEventListener('keydown', onKeyDownHandler);
      }
    };
  }, [disabled, groupDisabled, onKeyDownHandler]);
  useEffect(function () {
    setRadioId("gz-radio__input__".concat(name, "__").concat(value.toString()));
  }, [name, value]);
  var placementToRender = placement || groupPlacement;
  var disabledToRender = disabled || groupDisabled;
  return /*#__PURE__*/jsxs(Container, _objectSpread(_objectSpread(_objectSpread({
    ref: radioRef,
    role: "radio",
    className: className,
    placement: placementToRender,
    onFocus: onFocusHandler,
    onBlur: onBlurHandler,
    "aria-checked": isSelected,
    "aria-disabled": disabledToRender,
    "data-selected": isSelected,
    "data-disabled": disabledToRender,
    "data-invalid": invalid,
    "data-focused": isFocused // eslint-disable-next-line react/jsx-props-no-spreading

  }, ariaAttributes), dataAttributes), {}, {
    children: [/*#__PURE__*/jsx(Input, {
      id: radioId,
      name: name,
      defaultChecked: isSelected,
      value: value,
      disabled: disabledToRender,
      invalid: invalid,
      onClick: onClickHandler
    }), isDefinedAndNotNull(AsComponent) ? /*#__PURE__*/jsx(AsComponent, _objectSpread(_objectSpread({
      radioId: radioId,
      value: value,
      selected: isSelected,
      disabled: disabledToRender,
      invalid: invalid,
      placement: placementToRender,
      "aria-hidden": "true",
      onClick: onChangeHandler
    }, otherProps), {}, {
      children: children
    })) : /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Wrapper, {
        children: /*#__PURE__*/jsx(Dot, {})
      }), children && /*#__PURE__*/jsx(Label, {
        placement: placementToRender,
        htmlFor: radioId,
        children: children
      })]
    })]
  }));
});
Item$1.COMPONENT_ID = 'Gearz.Radio.Item';
Item$1.displayName = Item$1.COMPONENT_ID;
Item$1.Dot = Dot;
Item$1.propTypes = {
  as: PropTypes.elementTypeWithDescription('Component to be used as radio item. (use forwardedAs)'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  name: PropTypes.stringWithDescription('Name of the radio.'),
  value: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.number, PropTypes.bool], 'Value of the radio item.'),
  disabled: PropTypes.boolWithDescription('Flag to make the radio item disable.'),
  children: PropTypes.nodeWithDescription('Content of the radio item.'),
  placement: PropTypes.oneOfWithDescription(['top', 'right', 'bottom', 'left'], 'Placement of the label of the radio item.'),
  onClick: PropTypes.funcWithDescription('Callback called when click event is triggered.')
};
Item$1.defaultProps = {
  as: null,
  className: '',
  name: '',
  value: '',
  disabled: null,
  children: null,
  placement: null,
  onClick: noop
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Item = styled(Item$1).withConfig({
  componentId: "gz-radio-1679041977648__sc-12g5mtm-0"
})([""]);
Item.displayName = Item$1.displayName;
Item.propTypes = Item$1.propTypes;
Item.defaultProps = Item$1.defaultProps;

export { Container as C, Dot as D, Item as I, Label as L, RadioContext as R, Wrapper as W, Input as a };
